<script setup lang="ts">
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useForm } from '@volverjs/form-vue'
    import * as z from 'zod'
    import { PasswordSchema, SignUpSchema } from 'models'
    import type { AgentBranding, AuthMethods } from 'models'
    import { useImageFallback } from 'composables'

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const props = withDefaults(
        defineProps<{
            branding?: AgentBranding
            authMethods?: AuthMethods
            loading?: boolean
            /**
             * Set while an email sign-in waits for its second factor: the
             * methods the account has (`totp`, `otp`). The code step replaces
             * the forms until it is answered or cancelled.
             */
            twoFactorMethods?: string[]
        }>(),
        {
            branding: undefined,
            authMethods: undefined,
            loading: false,
            twoFactorMethods: undefined,
        },
    )

    // A deleted agent logo would leave a broken glyph above the sign-in form:
    // drop the whole block instead, the agent name below already identifies it.
    const { canShowImage, onImageError } = useImageFallback()

    const errorMessage = defineModel<string | undefined>('errorMessage', {
        default: undefined,
    })
    /**
     * Non-error feedback (e.g. "check your inbox" after sign-up / reset).
     * When set, a dedicated success screen replaces the form.
     */
    const success = defineModel<{ title: string; message: string } | undefined>(
        'success',
        { default: undefined },
    )

    const emit = defineEmits<{
        (
            e: 'sign-in',
            payload: {
                email: string
                password: string
                rememberMe: boolean
            },
        ): void
        (
            e: 'sign-up',
            payload: { email: string; password: string; name: string },
        ): void
        (e: 'request-reset', payload: { email: string }): void
        (e: 'social', provider: 'google' | 'microsoft'): void
        (
            e: 'verify-two-factor',
            payload: { kind: 'totp' | 'otp' | 'backup'; code: string },
        ): void
        (e: 'send-two-factor-code'): void
        (e: 'cancel-two-factor'): void
        (e: 'update:errorMessage', message?: string): void
        (e: 'update:success', value?: { title: string; message: string }): void
    }>()

    type Mode = 'sign-in' | 'sign-up' | 'reset'
    const mode = ref<Mode>('sign-in')
    watch(mode, () => {
        errorMessage.value = undefined
        success.value = undefined
    })

    /** Returns to the sign-in form, clearing any success feedback. */
    function backToSignIn() {
        success.value = undefined
        mode.value = 'sign-in'
    }

    const showSocialButtons = computed(
        () => props.authMethods?.google || props.authMethods?.microsoft,
    )

    const signInSchema = z.object({
        email: z.email(),
        password: PasswordSchema,
        rememberMe: z.boolean().default(false),
    })

    const resetSchema = z.object({
        email: z.email(),
    })

    const { VvForm: VvSignInForm, VvFormField: VvSignInField } = useForm(
        signInSchema,
        { lazyLoad: true },
    )
    const {
        VvForm: VvSignUpForm,
        VvFormField: VvSignUpField,
        clear: clearSignUp,
    } = useForm(SignUpSchema, { lazyLoad: true })
    const {
        VvForm: VvResetForm,
        VvFormField: VvResetField,
        clear: clearReset,
    } = useForm(resetSchema, { lazyLoad: true })

    // Empty the sign-up / reset forms once their submission succeeds so a
    // later "back to sign in" doesn't reveal stale input.
    watch(success, (value) => {
        if (value) {
            clearSignUp()
            clearReset()
        }
    })

    // Move focus to the success card once the flip animation has finished
    // (the out-in transition mounts it only after the form leaves), so
    // screen-reader/keyboard users are announced the result.
    const successCard = ref<HTMLElement>()
    function focusSuccessCard() {
        if (success.value) {
            successCard.value?.focus()
        }
    }

    function onSignIn(data: {
        email: string
        password: string
        rememberMe?: boolean
    }) {
        emit('sign-in', {
            email: data.email,
            password: data.password,
            rememberMe: data.rememberMe ?? false,
        })
    }

    function onSignUp(data: { name: string; email: string; password: string }) {
        emit('sign-up', {
            name: data.name.trim(),
            email: data.email,
            password: data.password,
        })
    }

    function onReset(data: { email: string }) {
        emit('request-reset', { email: data.email })
    }

    function handleSocial(provider: 'google' | 'microsoft') {
        emit('social', provider)
    }

    // Two-step verification (docs/specs/released/two-factor-authentication.md)
    type TwoFactorKind = 'totp' | 'otp' | 'backup'
    const isTwoFactorStep = computed(() => !!props.twoFactorMethods?.length)
    const twoFactorKind = ref<TwoFactorKind>('totp')
    const {
        VvForm: VvTwoFactorForm,
        VvFormField: VvTwoFactorField,
        clear: clearTwoFactor,
    } = useForm(z.object({ code: z.string().trim().min(6).max(32) }), {
        lazyLoad: true,
    })
    // A new challenge starts from the factor the account has, and the email
    // one sends its code straight away.
    watch(
        () => props.twoFactorMethods,
        (methods) => {
            if (!methods?.length) {
                return
            }
            twoFactorKind.value = methods.includes('totp') ? 'totp' : 'otp'
            clearTwoFactor()
            if (twoFactorKind.value === 'otp') {
                emit('send-two-factor-code')
            }
        },
        { immediate: true },
    )
    function switchTwoFactorKind(kind: TwoFactorKind) {
        twoFactorKind.value = kind
        errorMessage.value = undefined
        clearTwoFactor()
    }
    const twoFactorDescription = computed(() => {
        if (twoFactorKind.value === 'backup') {
            return $t('twoFactor.challengeBackup')
        }
        return twoFactorKind.value === 'otp'
            ? $t('twoFactor.challengeEmailSent')
            : $t('twoFactor.challengeApp')
    })
    function onVerifyTwoFactor(data: { code: string }) {
        emit('verify-two-factor', {
            kind: twoFactorKind.value,
            code: data.code.trim(),
        })
    }
</script>

<template>
    <div
        class="pk-chatbot-auth flex flex-col items-center justify-center w-full min-h-full p-lg overflow-y-auto">
        <div class="pk-chatbot-auth__flip w-full max-w-384 mx-auto my-auto">
            <Transition
                name="pk-flip"
                mode="out-in"
                @after-enter="focusSuccessCard">
                <!-- Success screen (sign-up / reset feedback) -->
                <div
                    v-if="success"
                    ref="successCard"
                    key="success"
                    tabindex="-1"
                    class="pk-chatbot-auth__flip-card outline-none flex flex-col text-center leading-normal">
                    <div
                        class="pk-chatbot-auth__success-icon w-64 h-64 border border-surface-3 rounded-full bg-surface flex items-center justify-center overflow-hidden bg-surface mx-auto mb-md">
                        <VvIcon
                            name="ri:mail-check-line"
                            aria-hidden="true"
                            class="text-success w-32 h-32" />
                    </div>
                    <h1 class="font-bold text-18 mb-xs">
                        {{ success.title }}
                    </h1>
                    <p class="mb-lg text-16 text-word-3">
                        {{ success.message }}
                    </p>
                    <VvButton
                        type="button"
                        modifiers="link"
                        icon="chevron-left"
                        :label="$t('action.backToSignIn')"
                        @click="backToSignIn" />
                </div>

                <!-- Sign-in / sign-up / reset forms -->
                <div
                    v-else
                    key="form"
                    class="pk-chatbot-auth__flip-card flex flex-col">
                    <!-- Form Container -->
                    <div class="flex flex-col">
                        <!-- Relative Header Logo -->
                        <div
                            v-if="canShowImage(branding?.interface.logo)"
                            class="flex justify-center mb-md">
                            <img
                                :src="branding?.interface.logo"
                                :alt="branding?.name"
                                class="w-64 h-64 border border-surface-3 rounded-full overflow-hidden bg-surface object-contain"
                                @error="
                                    onImageError(branding?.interface.logo)
                                " />
                        </div>

                        <div class="text-center leading-normal mb-lg">
                            <h1
                                v-if="branding?.name"
                                class="font-bold text-20 m-0">
                                {{ branding.name }}
                            </h1>
                            <component
                                :is="branding?.name ? 'h2' : 'h1'"
                                class="text-word-3 mb-0">
                                <template v-if="isTwoFactorStep">
                                    {{ $t('twoFactor.challengeTitle') }}
                                </template>
                                <template v-else-if="mode === 'sign-in'">
                                    {{ $t('auth.signInTitle') }}
                                </template>
                                <template v-else-if="mode === 'sign-up'">
                                    {{ $t('auth.signUpTitle') }}
                                </template>
                                <template v-else-if="mode === 'reset'">
                                    {{ $t('auth.resetTitle') }}
                                </template>
                            </component>
                        </div>

                        <!-- Error message -->
                        <VvAlert
                            v-if="errorMessage"
                            modifiers="danger"
                            class="mb-md"
                            :content="errorMessage" />

                        <!-- Two-step verification -->
                        <template v-if="isTwoFactorStep">
                            <p
                                class="text-14 text-word-3 text-center leading-normal mb-md">
                                {{ twoFactorDescription }}
                            </p>
                            <VvTwoFactorForm
                                class="flex flex-col"
                                :readonly="loading"
                                @submit="onVerifyTwoFactor">
                                <VvTwoFactorField
                                    name="code"
                                    type="text"
                                    :autocomplete="
                                        twoFactorKind === 'backup'
                                            ? 'off'
                                            : 'one-time-code'
                                    "
                                    :label="
                                        twoFactorKind === 'backup'
                                            ? $t('twoFactor.backupCode')
                                            : $t('twoFactor.code')
                                    "
                                    floating />
                                <VvButton
                                    type="submit"
                                    modifiers="primary"
                                    :loading="loading"
                                    :disabled="loading"
                                    :label="$t('twoFactor.verify')" />
                            </VvTwoFactorForm>
                            <div class="flex justify-center mt-md">
                                <VvButton
                                    v-if="twoFactorKind === 'totp'"
                                    type="button"
                                    modifiers="link"
                                    :label="$t('twoFactor.useBackupCode')"
                                    @click="switchTwoFactorKind('backup')" />
                                <VvButton
                                    v-else-if="twoFactorKind === 'backup'"
                                    type="button"
                                    modifiers="link"
                                    :label="$t('twoFactor.useApp')"
                                    @click="switchTwoFactorKind('totp')" />
                                <VvButton
                                    v-else
                                    type="button"
                                    modifiers="link"
                                    :disabled="loading"
                                    :label="$t('twoFactor.sendAgain')"
                                    @click="emit('send-two-factor-code')" />
                            </div>
                        </template>

                        <!-- Sign In -->
                        <VvSignInForm
                            v-else-if="mode === 'sign-in'"
                            class="flex flex-col"
                            :readonly="loading"
                            @submit="onSignIn">
                            <div class="mb-md">
                                <VvSignInField
                                    name="email"
                                    type="email"
                                    autocomplete="username"
                                    :label="$t('label.email')"
                                    floating />
                                <VvSignInField
                                    name="password"
                                    type="password"
                                    autocomplete="current-password"
                                    :label="$t('label.password')"
                                    floating />
                                <div class="mb-md">
                                    <VvButton
                                        type="button"
                                        modifiers="link"
                                        :label="$t('action.forgetPassword')"
                                        @click="mode = 'reset'" />
                                </div>
                                <VvSignInField
                                    name="rememberMe"
                                    type="checkbox"
                                    :label="$t('label.rememberMe')"
                                    switch />
                            </div>
                            <VvButton
                                type="submit"
                                modifiers="primary"
                                :loading="loading"
                                :disabled="loading"
                                :label="$t('action.signIn')" />
                        </VvSignInForm>

                        <!-- Sign Up -->
                        <VvSignUpForm
                            v-else-if="mode === 'sign-up'"
                            class="flex flex-col"
                            :readonly="loading"
                            @submit="onSignUp">
                            <div class="mb-md">
                                <VvSignUpField
                                    name="name"
                                    type="text"
                                    autocomplete="name"
                                    :label="$t('label.name')"
                                    floating />
                                <VvSignUpField
                                    name="email"
                                    type="email"
                                    autocomplete="username"
                                    :label="$t('label.email')"
                                    floating />
                                <VvSignUpField
                                    name="password"
                                    type="password"
                                    autocomplete="new-password"
                                    :label="$t('label.password')"
                                    floating />
                                <VvSignUpField
                                    name="passwordConfirmation"
                                    type="password"
                                    autocomplete="new-password"
                                    :label="$t('label.confirmPassword')"
                                    floating />
                            </div>
                            <VvButton
                                type="submit"
                                modifiers="primary"
                                :loading="loading"
                                :disabled="loading"
                                :label="$t('action.continue')" />
                        </VvSignUpForm>

                        <!-- Reset Password -->
                        <VvResetForm
                            v-else-if="mode === 'reset'"
                            class="flex flex-col"
                            :readonly="loading"
                            @submit="onReset">
                            <VvResetField
                                name="email"
                                type="email"
                                autocomplete="username"
                                :label="$t('label.email')"
                                floating />
                            <VvButtonGroup modifiers="vertical">
                                <VvButton
                                    type="submit"
                                    modifiers="primary"
                                    :loading="loading"
                                    :disabled="loading"
                                    :label="$t('action.submit')" />
                            </VvButtonGroup>
                        </VvResetForm>

                        <!-- Social providers (shared by sign-in and sign-up) -->
                        <template
                            v-if="
                                !isTwoFactorStep &&
                                (mode === 'sign-in' || mode === 'sign-up') &&
                                showSocialButtons
                            ">
                            <div class="vv-separator mt-xs py-xs">
                                <span>{{ $t('label.or') }}</span>
                            </div>
                            <VvButtonGroup
                                modifiers="vertical"
                                class="gap-xs mt-xs">
                                <VvButton
                                    v-if="authMethods?.google"
                                    type="button"
                                    modifiers="secondary"
                                    icon="logos:google-icon"
                                    :disabled="loading"
                                    :label="
                                        $t('action.signInWithSocialProvider', {
                                            provider: 'Google',
                                        })
                                    "
                                    @click="handleSocial('google')" />
                                <VvButton
                                    v-if="authMethods?.microsoft"
                                    type="button"
                                    modifiers="secondary"
                                    icon="logos:microsoft-icon"
                                    :disabled="loading"
                                    :label="
                                        $t('action.signInWithSocialProvider', {
                                            provider: 'Microsoft',
                                        })
                                    "
                                    @click="handleSocial('microsoft')" />
                            </VvButtonGroup>
                        </template>
                    </div>

                    <!-- Footer below Form -->
                    <div class="pt-lg text-center text-word-3">
                        <template v-if="isTwoFactorStep">
                            <VvButton
                                type="button"
                                modifiers="link"
                                icon="chevron-left"
                                :label="$t('action.backToSignIn')"
                                @click="emit('cancel-two-factor')" />
                        </template>
                        <template v-else-if="mode === 'sign-in'">
                            {{ $t('auth.noAccount') }}
                            <VvButton
                                type="button"
                                modifiers="link"
                                class="font-semibold"
                                :label="$t('action.signUp')"
                                @click="mode = 'sign-up'" />
                        </template>
                        <template v-else-if="mode === 'sign-up'">
                            {{ $t('auth.haveAccount') }}
                            <VvButton
                                type="button"
                                modifiers="link"
                                class="font-semibold"
                                :label="$t('action.signIn')"
                                @click="mode = 'sign-in'" />
                        </template>
                        <template v-else>
                            <VvButton
                                type="button"
                                modifiers="link"
                                icon="chevron-left"
                                :label="$t('action.backToSignIn')"
                                @click="mode = 'sign-in'" />
                        </template>
                    </div>
                </div>
            </Transition>
        </div>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-auth {
        // Flip-card reveal between the form and the success screen, mirroring
        // the classic login in apps/frontend.
        &__flip {
            display: grid;
            transform-style: preserve-3d;
            perspective: 1000px;
        }

        &__flip-card {
            grid-area: 1 / 1;
            backface-visibility: hidden;
            transform-style: preserve-3d;
        }

        // Subtle pop on the success check icon once the card has flipped in.
        &__success-icon {
            animation: pk-chatbot-auth-check-pop 0.4s ease-out 0.3s backwards;
        }
    }

    .pk-flip-enter-active {
        animation: pk-chatbot-auth-flip-in 0.6s ease-out;
    }

    .pk-flip-leave-active {
        animation: pk-chatbot-auth-flip-out 0.6s ease-in;
    }

    @keyframes pk-chatbot-auth-flip-in {
        0% {
            transform: rotateY(-180deg);
            opacity: 0;
        }

        100% {
            transform: rotateY(0deg);
            opacity: 1;
        }
    }

    @keyframes pk-chatbot-auth-flip-out {
        0% {
            transform: rotateY(0deg);
            opacity: 1;
        }

        100% {
            transform: rotateY(180deg);
            opacity: 0;
        }
    }

    @keyframes pk-chatbot-auth-check-pop {
        0% {
            transform: scale(0);
            opacity: 0;
        }

        60% {
            transform: scale(1.15);
        }

        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
</style>
